import { Box, Typography, useTheme, Divider } from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import ProductionQuantityLimitsIcon from "@mui/icons-material/ProductionQuantityLimits";
import GroupIcon from "@mui/icons-material/Group";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  calculateEndTime,
  calculateEndTimes,
  convertToSeconds,
  format_Number,
} from "../../utils/utils";

const YourBookingCard = ({
  selectedDate,
  reservType,
  time,
  adult,
  child,
  kid,
  baby,
  toddler,
  selectedExtraProduct,
  languageWords,
  totalCostYourBooking,
}) => {
  const theme = useTheme();
  const{activityDuration}=useSelector((store)=> store?.Activity)
  dayjs.extend(advancedFormat);
  const { widgetConfig,resType } = useSelector((store) => store?.widgetSettings);
  const totalSum = selectedExtraProduct.reduce(
    (acc, product) => acc + product.quantity * product.price,
    0
  );

  const formattedDate = dayjs(selectedDate, "DD-MM-YYYY").format(
    "dddd, DD MMMM YYYY"
  );
  const formatTranslatedDate = (selectedDate, languageWords) => {
    const formattedDate = dayjs(selectedDate, "DD-MM-YYYY").format(
      "dddd, DD MMMM YYYY"
    );

    // Replace day and month with translations
    const translatedDate = formattedDate
      .replace(
        /\b(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\b/g,
        (match) =>
          languageWords?.[match == "Monday" ? "monday" : match] || match
      )
      .replace(
        /\b(January|February|March|April|May|June|July|August|September|October|November|December)\b/g,
        (match) => languageWords?.[match] || match
      );

    return translatedDate;
  };
  const { endTime } = calculateEndTimes(time,activityDuration?.timeValue ? convertToSeconds(activityDuration?.timeValue) : reservType?.duration);

  return (
    <Box
      sx={{
        border: `1px dotted ${theme?.palette?.detailsBorderColor}`,
        backgroundColor: theme?.palette?.background?.paper,
        borderRadius: "12px",
        p: 3,
        width: "100%",
        boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Typography
        variant="h6"
        fontWeight="bold"
        color={theme?.details?.detailsHeaderColor}
      >
        {reservType?.name}
      </Typography>
      {
        resType == 'ticketing' ? 
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 3 }}>
        <AccessTimeIcon sx={{ color: theme?.details?.detailsDateIconColor }} />
        <Box>
          <Typography variant="body1" color={theme?.palette?.text?.secondary}>
            {reservType?.eventDateAndTime}
          </Typography>
          <Box variant="body1" color={theme?.palette?.text?.secondary}>
            {selectedDate && !reservType?.isStandardTicket && (
              <Typography
                sx={{
                  color: theme?.details?.detailsDateTextColor,
                  fontSize: "14px",
                  fontWeight: "500", // Slightly bold for better emphasis
                  display: "flex",
                  justifyContent: "flex-start", // Centers the content horizontally
                  alignItems: "center", // Aligns the text vertically
                  gap: "0.5rem", // Adds space between "From" and "To"
                  mt: 1,
                }}
              >
                {languageWords?.From}:{" "}
                <span
                  style={{
                    fontWeight: "bold",
                    color: theme?.details?.detailsDateTextColor,
                  }}
                >
                  {`${dayjs(selectedDate?.fromTime, "HH:mm:ss").format(
                    "HH:mm"
                  )}`}
                </span>{" "}
                {languageWords?.To}:{" "}
                <span
                  style={{
                    fontWeight: "bold",
                    color: theme?.details?.detailsDateTextColor,
                  }}
                >
                  {`${dayjs(selectedDate?.toTime, "HH:mm:ss").format("HH:mm")}`}
                </span>
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
      :
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 3 }}>
      <AccessTimeIcon sx={{ color: theme?.details?.detailsDateIconColor }} />
      <Box>
        <Typography variant="body1" color={theme?.palette?.text?.secondary}>
          {formatTranslatedDate(selectedDate, languageWords)}
        </Typography>
        <Typography
          sx={{
            color: theme?.details?.detailsDateTextColor,
            fontSize: "14px",
            fontWeight: "500", // Slightly bold for better emphasis
            display: "flex",
            justifyContent: "flex-start", // Centers the content horizontally
            alignItems: "center", // Aligns the text vertically
            gap: "0.5rem", // Adds space between "From" and "To"
            mt: 1,
          }}
        >
          {languageWords?.From}:{" "}
          <span
            style={{
              fontWeight: "bold",
              color: theme?.details?.detailsDateTextColor,
            }}
          >
            {time?.substring(0, 5)}
          </span>{" "}
          {languageWords?.To}:{" "}
          <span
            style={{
              fontWeight: "bold",
              color: theme?.details?.detailsDateTextColor,
            }}
          >
            {endTime}
          </span>
        </Typography>
      </Box>
    </Box>

      }

 

      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 2 }}>
        <GroupIcon sx={{ color: theme?.details?.detailsDateIconColor }} />
        <Typography variant="body1" color={theme?.palette?.text?.secondary}>
          {[
            adult && `${adult} ${widgetConfig?.fields?.field1Text}`,
            child && `${child}  ${widgetConfig?.fields?.field2Text}`,
            kid && `${kid}  ${widgetConfig?.fields?.field3Text}`,
            toddler && `${toddler}  ${widgetConfig?.fields?.field5Text}`,
            baby && `${baby}  ${widgetConfig?.fields?.field4Text}`,
          ]
            ?.filter(Boolean)
            ?.join(", ")}
        </Typography>
      </Box>

      {selectedExtraProduct?.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <ProductionQuantityLimitsIcon color="warning" />
            <Typography variant="body1" fontWeight="bold">
              {languageWords["ExtraProducts"]}
            </Typography>
          </Box>
          {selectedExtraProduct?.map((item) => (
            <Box
              key={item.idproduct}
              sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}
            >
              <Typography
                variant="body2"
                color={theme?.palette?.text?.secondary}
              >
                {item?.quantity}x {item?.productName}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                color={theme?.details?.detailsPriceColor}
              >
                &euro;{format_Number((item?.quantity * item?.price).toFixed(2))}
              </Typography>
            </Box>
          ))}
        </>
      )}

      {totalCostYourBooking > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ color: theme?.details?.detailsHeaderColor }}
            >
              {reservType?.name}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: theme?.details?.detailsPriceColor,
                fontSize: "16px",
                fontWeight: "700",
              }}
            >
              €{format_Number((totalCostYourBooking).toFixed(2))}
            </Typography>
          </Box>
        </>
      )}
    </Box>
  );
};

export default YourBookingCard;
